import { get_manual_decision_status } from './manage_verification_helpers';
import {
  build_field_run_by_question,
  build_manual_review_sections_from_form_sections as build_manual_review_sections_from_sections_helper,
  decorate_review_form_section
} from './manage_verification_manual_section_helpers';
import { find_submitted_answer_for_mapping } from './manage_verification_manual_answer_helpers';

export function get_manual_workspace_decision_warning(manual_item, reviewer_comments) {
  if (!manual_item) return 'No manual verification record is available for this case.';
  if (!reviewer_comments) return 'Reviewer comments are required before submitting the decision.';
  if (!Number(manual_item?.run_id || 0)) return 'Start the manual review first so a review run can be opened.';
  return '';
}

export function build_manual_workspace_decision_payload(manual_item, action, reviewer_comments) {
  return {
    run_id: Number(manual_item.run_id),
    status: get_manual_decision_status(action),
    decision_reason: reviewer_comments
  };
}

function build_manual_mapping_question(mapping = {}, resolve_answer) {
  const question_id = Number(mapping?.manual_section_question || 0)
    || Number(mapping?.verification_section_question || 0)
    || Number(mapping?.onboarding_section_question || 0);
  const submitted_answer = typeof resolve_answer === 'function' ? resolve_answer(mapping) : {};
  const submitted_answer_data = submitted_answer && typeof submitted_answer === 'object'
    ? submitted_answer
    : { answer: submitted_answer };
  return {
    id: question_id,
    label: mapping?.manual_question_text
      || mapping?.verification_question_text
      || mapping?.onboarding_question_text
      || 'Verification field',
    answer: submitted_answer_data.answer || '',
    file_url: submitted_answer_data.file_url || '',
    file_name: submitted_answer_data.file_name || '',
    type: submitted_answer_data.type || '',
    mapping,
    source_label: mapping?.onboarding_question_text || ''
  };
}

export function manual_field_key(item = {}) {
  return String(item?.id || item?.section_question_id || 'manual-field');
}

export function manual_choice_status(match_status = '') {
  const normalized = String(match_status || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!normalized) return '';
  if (['match', 'matched', 'yes', 'pass', 'passed', 'verified', 'success'].includes(normalized)) return 'pass';
  if (['mismatch', 'no', 'fail', 'failed', 'rejected', 'reject', 'error'].includes(normalized)) return 'fail';
  return 'na';
}

function normalize_manual_run_status(value = '') {
  if (value && typeof value === 'object') {
    const nested_value = value.status || value.name || value.label || value.decision_status || '';
    return String(nested_value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
  }
  return String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
}

function get_manual_status_candidate(source = {}) {
  return (
    source?.decision_status
    || source?.status
    || source?.decision
    || source?.outcome
    || ''
  );
}

function get_manual_run_status(manual_item = {}) {
  return normalize_manual_run_status(
    get_manual_status_candidate(manual_item?.manual_run)
      || get_manual_status_candidate(manual_item?.raw_status)
      || get_manual_status_candidate(manual_item)
  );
}

export function get_manual_outcome_status(manual_item = {}) {
  return normalize_manual_run_status(
    get_manual_status_candidate(manual_item?.manual_run)
      || get_manual_status_candidate(manual_item?.raw_status)
      || get_manual_status_candidate(manual_item)
  );
}

export function get_manual_outcome_label(manual_item = {}) {
  const status = get_manual_outcome_status(manual_item);
  if (['pass', 'passed', 'approved', 'approve'].includes(status)) return 'Approved';
  if (['partial', 'request changes', 'request change', 'changes requested'].includes(status)) return 'Changes requested';
  if (['fail', 'failed', 'rejected', 'reject'].includes(status)) return 'Rejected';
  if (status === 'completed') return 'Completed';
  if (!status) return '';
  return status.replace(/\b\w/g, (char) => char.toUpperCase());
}

export function get_manual_outcome_tone(manual_item = {}) {
  const status = get_manual_outcome_status(manual_item);
  if (['pass', 'passed', 'approved', 'approve'].includes(status)) return 'success';
  if (['partial', 'request changes', 'request change', 'changes requested'].includes(status)) return 'warning';
  if (['fail', 'failed', 'rejected', 'reject'].includes(status)) return 'danger';
  return 'secondary';
}

export function get_manual_outcome_action(manual_item = {}) {
  const status = get_manual_outcome_status(manual_item);
  if (['pass', 'passed', 'approved', 'approve', 'complete', 'completed'].some((word) => status.includes(word))) {
    return 'approve';
  }
  if (['change', 'changes', 'remed', 'request info', 'request information'].some((word) => status.includes(word))) {
    return 'request_changes';
  }
  if (['reject', 'rejected', 'fail', 'failed'].some((word) => status.includes(word))) {
    return 'reject';
  }
  return '';
}

export function manual_run_is_terminal(manual_item = {}) {
  return ['pass', 'passed', 'fail', 'failed', 'approved', 'rejected', 'complete', 'completed']
    .includes(get_manual_run_status(manual_item));
}

export function get_terminal_manual_run_message(manual_item = {}) {
  const status = get_manual_run_status(manual_item) || 'complete';
  return `This manual review is already ${status}. Field outcomes are read-only.`;
}

export function is_terminal_manual_run_error(error) {
  const message = error?.response?.data?.message || error?.message || '';
  return String(message).toLowerCase().includes('already terminal');
}

export function build_manual_field_choice_payload(manual_item, item, match_status) {
  return {
    run_id: Number(manual_item.run_id),
    section_question_id: Number(item?.section_question_id || 0),
    user_answer: item?.answer || '',
    verified_value: match_status === 'match' ? item?.answer || '' : '',
    match_status,
    reviewer_comment: item?.comment || '',
    source_evidence: item?.source_label || item?.detail || ''
  };
}

function question_has_id(question) {
  return Number(question.id || 0) > 0;
}

export function build_manual_mapping_questions(mappings = [], resolve_answer) {
  return mappings
    .map((mapping) => build_manual_mapping_question(mapping, resolve_answer))
    .filter(question_has_id);
}

export function format_manual_review_rule_name(value = '') {
  const normalized_value = String(value || 'Manual verification')
    .replace(/[_-]+/g, ' ')
    .trim();
  const title_value = normalized_value.replace(/\w\S*/g, (word) => (
    word.toUpperCase() === word
      ? word
      : `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
  ));
  return title_value || 'Manual Verification';
}

export function get_manual_review_rule_name(item = {}) {
  return format_manual_review_rule_name(
    item?.manual_binding?.manual_verification_form_name
    || item?.manual_binding?.verification_form_name
    || item?.manual_verification_form_name
    || item?.manual_run?.manual_verification_form_name
    || 'Manual verification'
  );
}

export function build_manual_mapping_section_payload(item, questions) {
  return [{
    id: `manual-config-${item?.manual_binding?.id || item?.verification_type || 'review'}`,
    form_name: get_manual_review_rule_name(item),
    answered_count: questions.length,
    sections: [{
      id: 'configured-manual-verification',
      label: 'Mapped fields',
      sub_sections: [{
        id: 'configured-manual-verification-fields',
        label: 'Mapped fields',
        questions
      }]
    }]
  }];
}

export function manual_mappings_have_onboarding_links(mappings = []) {
  return (Array.isArray(mappings) ? mappings : []).some((mapping) => (
    Number(mapping?.onboarding_section_question || 0) > 0
    || String(mapping?.onboarding_question_text || '').trim() !== ''
  ));
}

function get_manual_mappings(item = {}) {
  return Array.isArray(item?.manual_mappings) ? item.manual_mappings : [];
}

export function pick_manual_mappings_from_items(primary_item = null, support_item = null) {
  const primary_mappings = get_manual_mappings(primary_item);
  const support_mappings = get_manual_mappings(support_item);

  if (!primary_mappings.length) return support_mappings;
  if (!support_mappings.length) return primary_mappings;

  const primary_has_links = manual_mappings_have_onboarding_links(primary_mappings);
  const support_has_links = manual_mappings_have_onboarding_links(support_mappings);

  if (support_has_links && !primary_has_links) return support_mappings;
  return primary_mappings;
}

export function build_manual_review_sections_from_form_sections(item = {}, manual_form_sections = [], manual_mappings = [], resolve_answer) {
  return build_manual_review_sections_from_sections_helper(
    item,
    manual_form_sections,
    manual_mappings,
    resolve_answer,
    get_manual_review_rule_name
  );
}

export {
  build_field_run_by_question,
  decorate_review_form_section,
  find_submitted_answer_for_mapping
};
